import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ModelPoolService } from '../../../services/model-pool.service';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { ConfigService } from 'src/app/services/config.service';
import { DialogService } from '../../../services/dialog.service';

/** Responsibility being added/edited */
export interface StimulusFormState {
  plugin: string;
  label: string;
  instruction: string;
  enabled: boolean;
  params: Record<string, any>;
  editingId: string | null; // null = new
}

/** Represents a single MCP server override configuration */
export interface McpServerOverride {
  name: string;
  command: string;
  args: string[];
  env: Record<string, string>;
  expanded: boolean; // UI state for collapsible card
}

@Component({
    selector: 'app-agent-settings-widget',
    templateUrl: './agent-settings-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './agent-settings-widget.component.scss'],
    standalone: false
})
export class AgentSettingsWidgetComponent extends BaseWidgetComponent implements OnInit {
  private dialogs = inject(DialogService);
  @Input() agentId: string | null = null; // null = create new agent
  @Input() team: string | null = null;    // venue a new agent is born in (from the Agents app); null = ambient team
  @Input() editFileCallback?: (path: string) => void; // Callback for editing files (used in fullscreen mode)
  @Output() onAgentSaved = new EventEmitter<{ agentId: string }>();
  @Output() onAgentDeleted = new EventEmitter<{ agentId: string }>();
  @Output() onOpenBackendDetail = new EventEmitter<{ backendId: string; credentialsId?: string }>();
  @Output() onEditFile = new EventEmitter<{ parentId: string | null; path: string }>();

  // Form fields
  public name: string = '';
  public backend: string = 'claude-cli';
  public model: string = 'claude-sonnet-4-5-20250514';
  public credentialsId: string | null = null;
  public systemPrompt: string = '';
  public systemPromptFile: string | null = null;
  public temperature: number = 0.7;
  // Durable heartbeat on/off — the composer heart button's replacement.
  // New agents default to LISTENING (the born-deaf default was the Sans bug:
  // creation resolved heartbeat_enabled to False and settings could never fix it).
  public heartbeatEnabled: boolean = true;
  private _heartbeatSnapshot: boolean | null = null;
  public maxTokens: number | null = null;
  public mcpServers: string[] = [];
  public mcpServerOverrides: McpServerOverride[] = [];
  public disabledSkills: string[] = [];

  // Available options
  public availableBackends = (() => {
    const all = ['claude-cli', 'claude-interactive', 'claude', 'ollama', 'gemini', 'gemini-cli', 'antigravity', 'chatgpt', 'deepseek'];
    const allowed = ConfigService.allowedBackends;
    return allowed ? all.filter(b => allowed.includes(b)) : all;
  })();
  public availableModels: MuxAgentService.ModelInfo[] = [];

  // The model POOL (2026-08-13 design): one picker chooses the (model,
  // credential) tuple; backend + credentials_id are derived, not chosen.
  public poolGroups: ModelPoolService.PoolGroup[] = [];
  poolFilter = '';
  filteredPoolGroups: ModelPoolService.PoolGroup[] = [];

  private _poolFilterTimer: any = null;

  onPoolFilter(value: string): void {
    this.poolFilter = value;
    clearTimeout(this._poolFilterTimer);
    this._poolFilterTimer = setTimeout(() => this._applyPoolFilter(), 120);
  }

  private _applyPoolFilter(): void {
    const q = this.poolFilter.trim().toLowerCase();
    this.filteredPoolGroups = !q ? this.poolGroups
      : this.poolGroups
          .map(g => ({ provider: g.provider, models: g.models.filter(mm =>
              [mm.displayName, mm.credentialName || '', g.provider]
                .some(t => t.toLowerCase().includes(q))) }))
          .filter(g => g.models.length);
    // The panel is an embedded view in the CDK overlay — force a synchronous
    // pass so the open panel re-renders while the user types.
    this.cdr.detectChanges();
  }

  onPoolFilterKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      // Escape with text = clear the filter, keep the panel open.
      // Escape with an empty box falls through and closes the panel.
      if (this.poolFilter) {
        e.stopPropagation();
        e.preventDefault();
        clearTimeout(this._poolFilterTimer);
        this.poolFilter = '';
        this._applyPoolFilter();
      }
      return;
    }
    e.stopPropagation();
  }

  onPoolPanelToggle(open: boolean): void {
    if (open) {
      // Type-to-filter should just work: focus the filter box once the CDK
      // overlay has rendered (only one panel is ever open).
      setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('.model-select-panel .pool-filter input');
        el?.focus();
      });
    } else if (this.poolFilter) {
      this.onPoolFilter('');
    }
  }
  public selectedPoolValue: string | null = null;

  public get selectedPoolEntry(): ModelPoolService.PoolEntry | null {
    for (const g of this.poolGroups) {
      const e = g.models.find(x => x.key === this.selectedPoolValue);
      if (e) return e;
    }
    return null;
  }

  public onPoolPick(value: string): void {
    this.selectedPoolValue = value;
    const e = this.selectedPoolEntry;
    if (!e) return;
    this.backend = e.backend;
    this.model = e.model;
    this.credentialsId = e.credentialId;
  }

  private _syncPoolSelection(): void {
    // Find the tuple matching the agent's current (backend, model, credential);
    // fall back to first (backend, model) match so legacy agents still resolve.
    let match: ModelPoolService.PoolEntry | null = null;
    for (const g of this.poolGroups) {
      for (const e of g.models) {
        const backendMatch = e.backend === this.backend ||
          ((e.backend === 'claude-cli' || e.backend === 'claude-interactive') &&
           (this.backend === 'claude-cli' || this.backend === 'claude-interactive'));
        if (backendMatch && e.model === this.model) {
          if ((e.credentialId || '') === (this.credentialsId || '')) { match = e; break; }
          if (!match) match = e;
        }
      }
      if (match && (match.credentialId || '') === (this.credentialsId || '')) break;
    }
    this.selectedPoolValue = match?.key ?? null;
  }
  public allModels: Record<string, MuxAgentService.ModelInfo[]> = {};
  public availableMcpServers: MuxAgentService.McpServer[] = [];
  public availableSkills: { name: string; path: string; manageable?: boolean }[] = [];

  // Tab state
  public activeTab: 'role' | 'responsibilities' | 'advanced' = 'role';

  // Responsibilities state
  public agentStimuli: MuxAgentService.AgentStimulus[] = [];
  public availableStimuliPlugins: MuxAgentService.StimulusPlugin[] = [];
  public stimulusForm: StimulusFormState | null = null; // non-null = form open
  private originalStimuliJson: string = '[]';

  // Primary agent
  public isPrimaryAgent = false;

  // State
  public isLoading = false;
  public isSaving = false;
  public isReloading = false;
  public isNewAgent = true;
  public isDeleted = false;  // Shows placeholder after agent is deleted
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public saveStatus: 'CLEAN' | 'DIRTY' | 'SAVING' | 'ERROR' = 'CLEAN';

  // Original values for dirty tracking
  private originalValues: {
    name: string;
    backend: string;
    model: string;
    systemPrompt: string;
    temperature: number;
    maxTokens: number | null;
    mcpServers: string[];
    mcpServerOverrides: string; // JSON stringified for comparison
    disabledSkills: string[];
  } | null = null;

  // Sensitive env var patterns (show as password fields)
  private sensitivePatterns = ['TOKEN', 'SECRET', 'KEY', 'PASSWORD', 'API_KEY', 'APIKEY'];

  // Track which sensitive env vars have their values visible
  public visibleEnvVars: Set<string> = new Set();

  constructor(
        private cdr: ChangeDetectorRef,
private _muxAgent: MuxAgentService,
    private _cdr: ChangeDetectorRef,
    private _pool: ModelPoolService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this._pool.groups().then(g => {
      this.poolGroups = g;
      this.onPoolFilter(this.poolFilter);
      this._syncPoolSelection();
      this._cdr.markForCheck();
    }).catch(() => {});

    try {
      // Load models, MCP servers, skills, skill status, and stimulus plugins in parallel
      const [modelsResponse, mcpServers, skillsResponse, skillsStatus, pluginsResponse] = await Promise.all([
        this._muxAgent.getModels(),
        this._muxAgent.getMcpServers(),
        this._muxAgent.getSkills(),
        this._muxAgent.getSkillsStatus(),
        this._muxAgent.getAvailableStimuliPlugins()
      ]);

      if (modelsResponse) {
        this.allModels = modelsResponse.models;
        this.updateModelsForBackend();
      }

      this.availableMcpServers = mcpServers;
      this.availableSkills = skillsResponse?.skills || [];
      this.availableStimuliPlugins = (pluginsResponse || []).filter((p: MuxAgentService.StimulusPlugin) => !p.system);

      // Load existing agent if editing, or fetch default prompt for new agent
      if (this.agentId) {
        this.isNewAgent = false;
        const [agent, stimuli, primaryAgentId] = await Promise.all([
          this._muxAgent.getAgent(this.agentId),
          this._muxAgent.getAgentStimuli(this.agentId),
          this._muxAgent.getPrimaryAgent()
        ]);
        if (agent) {
          this.populateFromAgent(agent);
          // Override disabled_skills with actual symlink state from disk
          if (skillsStatus) {
            this.disabledSkills = skillsStatus.disabled;
            this.originalValues.disabledSkills = [...this.disabledSkills];
          }
        }
        this.isPrimaryAgent = primaryAgentId === this.agentId;
        this.agentStimuli = stimuli;
        this.originalStimuliJson = JSON.stringify(this._serializeStimuli());
      } else {
        // New agent - fetch and pre-populate default system prompt
        const defaultPrompt = await this._muxAgent.getDefaultPrompt(this.model);
        if (defaultPrompt) {
          this.systemPrompt = defaultPrompt;
        }
      }
    } catch (error) {
      console.error('Failed to initialize agent settings:', error);
      this.errorMessage = 'Failed to load agent settings';
    } finally {
      this.isLoading = false;
      // Trigger change detection - required when widget is dynamically created
      // in an OnPush parent component (chat-screen in fullscreen mode)
      this._cdr.detectChanges();
    }
  }

  private populateFromAgent(agent: MuxAgentService.Agent): void {
    this.name = agent.name;
    this.backend = agent.backend;
    this.model = agent.model;
    this.credentialsId = agent.credentials_id || null;
    this._syncPoolSelection();
    this.systemPrompt = agent.system_prompt || '';
    this.systemPromptFile = agent.system_prompt_file || null;
    this.temperature = agent.temperature ?? 0.7;
    this.heartbeatEnabled = (agent as any).heartbeat_enabled ?? true;
    // Snapshot for touched-detection: heartbeat state can change OUTSIDE this
    // form (throttle menu, another tab) while it's open — Save only carries
    // heartbeat_enabled when the user actually moved the toggle, so a stale
    // snapshot can never silently disable a running agent.
    this._heartbeatSnapshot = this.heartbeatEnabled;
    this.maxTokens = agent.max_tokens || null;
    this.mcpServers = [...(agent.mcp_servers || [])];
    this.mcpServerOverrides = this.parseServerOverrides(agent.mcp_server_overrides);
    this.disabledSkills = [...(agent.disabled_skills || [])];
    this.updateModelsForBackend();

    // Store original values for dirty tracking
    this.originalValues = {
      name: this.name,
      backend: this.backend,
      model: this.model,
      systemPrompt: this.systemPrompt,
      temperature: this.temperature,
      maxTokens: this.maxTokens,
      mcpServers: [...this.mcpServers],
      mcpServerOverrides: JSON.stringify(this.serializeServerOverrides()),
      disabledSkills: [...this.disabledSkills]
    };
  }

  /**
   * Parse mcp_server_overrides from agent config into UI-friendly format
   */
  private parseServerOverrides(overrides: Record<string, any> | undefined): McpServerOverride[] {
    if (!overrides) return [];

    return Object.entries(overrides).map(([name, config]) => ({
      name,
      command: config.command || '',
      args: config.args || [],
      env: config.env || {},
      expanded: false
    }));
  }

  /**
   * Serialize UI format back to agent config format
   */
  private serializeServerOverrides(): Record<string, any> {
    const result: Record<string, any> = {};
    for (const override of this.mcpServerOverrides) {
      result[override.name] = {
        command: override.command,
        args: override.args,
        env: override.env
      };
    }
    return result;
  }

  public get isDirty(): boolean {
    if (this.isNewAgent) {
      // For new agents, dirty if name is filled
      return this.name.trim().length > 0;
    }
    if (!this.originalValues) return false;

    return (
      this.name !== this.originalValues.name ||
      this.backend !== this.originalValues.backend ||
      this.model !== this.originalValues.model ||
      this.systemPrompt !== this.originalValues.systemPrompt ||
      this.temperature !== this.originalValues.temperature ||
      this.maxTokens !== this.originalValues.maxTokens ||
      JSON.stringify(this.mcpServers.sort()) !== JSON.stringify(this.originalValues.mcpServers.sort()) ||
      JSON.stringify(this.serializeServerOverrides()) !== this.originalValues.mcpServerOverrides ||
      JSON.stringify(this.disabledSkills.sort()) !== JSON.stringify(this.originalValues.disabledSkills.sort()) ||
      JSON.stringify(this._serializeStimuli()) !== this.originalStimuliJson
    );
  }

  public onBackendChange(): void {
    this.updateModelsForBackend();
    // Select first model if current model not available for new backend
    if (this.availableModels.length > 0 && !this.availableModels.find(m => m.name === this.model)) {
      this.model = this.availableModels[0].name;
    }
  }

  private updateModelsForBackend(): void {
    this.availableModels = this.allModels[this.backend] || [];
  }

  public isMcpServerEnabled(serverName: string): boolean {
    return this.mcpServers.includes(serverName);
  }

  public toggleMcpServer(serverName: string): void {
    const index = this.mcpServers.indexOf(serverName);
    if (index >= 0) {
      this.mcpServers.splice(index, 1);
    } else {
      this.mcpServers.push(serverName);
    }
  }

  public async save(): Promise<void> {
    if (!this.name.trim()) {
      this.errorMessage = 'Agent name is required';
      return;
    }

    this.isSaving = true;
    this.saveStatus = 'SAVING';
    this.errorMessage = null;

    // Check if MCP overrides or skills changed (need to reload agent after save)
    const newOverridesJson = JSON.stringify(this.serializeServerOverrides());
    const mcpOverridesChanged = !this.isNewAgent &&
      this.originalValues &&
      newOverridesJson !== this.originalValues.mcpServerOverrides;
    const skillsChanged = !this.isNewAgent &&
      this.originalValues &&
      JSON.stringify(this.disabledSkills.sort()) !== JSON.stringify(this.originalValues.disabledSkills.sort());

    try {
      const config: Partial<MuxAgentService.Agent> = {
        id: this.isNewAgent ? this._muxAgent.generateAgentId(this.name) : this.agentId!,
        name: this.name,
        backend: this.backend,
        model: this.model,
        credentials_id: this.credentialsId ?? undefined,
        system_prompt: this.systemPrompt || undefined,
        temperature: this.temperature,
        // New agent: always send (listening on by default). Existing agent:
        // only when the user moved the toggle — omitted means "preserve",
        // so out-of-band changes made while this form sat open survive Save.
        ...(this.isNewAgent || this.heartbeatEnabled !== this._heartbeatSnapshot
          ? { heartbeat_enabled: this.heartbeatEnabled } : {}),
        max_tokens: this.maxTokens || undefined,
        mcp_servers: this.mcpServers,
        mcp_server_overrides: this.mcpServerOverrides.length > 0 ? this.serializeServerOverrides() : undefined,
        disabled_skills: this.disabledSkills,
        heartbeat_stimuli: this._serializeStimuli()
      };

      if (this.isNewAgent) {
        const created = await this._muxAgent.createAgent(config, this.team ?? undefined);
        // After creating, update state to reflect we're now editing
        this.isNewAgent = false;
        this.agentId = config.id!;
        if (created) {
          this.credentialsId = created.credentials_id || null;
        }
      } else {
        await this._muxAgent.updateAgent(this.agentId!, config);
      }

      // Update original values so form is no longer dirty
      this._heartbeatSnapshot = this.heartbeatEnabled;
      this.originalValues = {
        name: this.name,
        backend: this.backend,
        model: this.model,
        systemPrompt: this.systemPrompt,
        temperature: this.temperature,
        maxTokens: this.maxTokens,
        mcpServers: [...this.mcpServers],
        mcpServerOverrides: newOverridesJson,
        disabledSkills: [...this.disabledSkills]
      };
      this.originalStimuliJson = JSON.stringify(this._serializeStimuli());

      this.saveStatus = 'CLEAN';
      this.onAgentSaved.emit({ agentId: config.id! });

      // Auto-reload agent if MCP config or skills changed so changes take effect immediately
      if (mcpOverridesChanged || skillsChanged) {
        await this._muxAgent.reloadAgent(this.agentId!);
        const changedItems = [
          mcpOverridesChanged ? 'MCP config' : null,
          skillsChanged ? 'skills' : null
        ].filter(Boolean).join(' and ');
        this.successMessage = `Saved and reloaded agent with new ${changedItems}`;
        setTimeout(() => { this.successMessage = null; }, 3000);
      }
    } catch (error) {
      console.error('Failed to save agent:', error);
      this.errorMessage = 'Failed to save agent. Please try again.';
      this.saveStatus = 'ERROR';
    } finally {
      this.isSaving = false;
    }
  }

  public async deleteAgent(): Promise<void> {
    if (this.isNewAgent || !this.agentId) return;

    if (!await this.dialogs.confirm(`Delete agent "${this.name}"? Sessions will be preserved.`)) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;

    try {
      await this._muxAgent.deleteAgent(this.agentId, false);
      this.onAgentDeleted.emit({ agentId: this.agentId });
      this.isDeleted = true;  // Show deleted placeholder
      this.close();  // Also try to close (works in grid mode)
    } catch (error) {
      console.error('Failed to delete agent:', error);
      this.errorMessage = 'Failed to delete agent. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  public async reload(): Promise<void> {
    if (this.isNewAgent || !this.agentId) return;
    this.isReloading = true;
    this.errorMessage = null;
    try {
      await this._muxAgent.reloadAgent(this.agentId);
      this.successMessage = 'Agent reloaded.';
      setTimeout(() => this.successMessage = null, 3000);
    } catch (error) {
      console.error('Failed to reload agent:', error);
      this.errorMessage = 'Failed to reload agent.';
    } finally {
      this.isReloading = false;
    }
  }

  public async togglePrimaryAgent(): Promise<void> {
    if (!this.agentId) return;
    const newValue = !this.isPrimaryAgent;
    try {
      await this._muxAgent.setPrimaryAgent(newValue ? this.agentId : null);
      this.isPrimaryAgent = newValue;
      this.successMessage = newValue
        ? `${this.name} is now the primary agent`
        : `${this.name} is no longer the primary agent`;
      setTimeout(() => this.successMessage = null, 3000);
    } catch (error) {
      console.error('Failed to toggle primary agent:', error);
      this.errorMessage = 'Failed to update primary agent setting';
    }
  }

  public getModelDisplayName(modelName: string): string {
    const model = this.availableModels.find(m => m.name === modelName);
    return model?.display_name || modelName;
  }

  public get widgetTitle(): string {
    return this.isNewAgent ? 'New Agent' : `Edit Agent: ${this.name}`;
  }

  public openBackendDetail(): void {
    this.onOpenBackendDetail.emit({ backendId: this.backend, credentialsId: this.credentialsId || undefined });
  }

  public openSystemPromptFile(): void {
    if (this.systemPromptFile) {
      // Use callback if available (fullscreen mode), otherwise emit event (grid mode)
      if (this.editFileCallback) {
        this.editFileCallback(this.systemPromptFile);
      } else {
        this.onEditFile.emit({ parentId: null, path: this.systemPromptFile });
      }
    }
  }

  // ============ MCP Server Overrides UI Methods ============

  /**
   * Toggle expansion of an MCP server override card
   */
  public toggleServerExpanded(server: McpServerOverride): void {
    server.expanded = !server.expanded;
  }

  /**
   * Get the env var keys for a server (for iteration in template)
   */
  public getEnvKeys(server: McpServerOverride): string[] {
    return Object.keys(server.env);
  }

  /**
   * Check if an env var key is sensitive (should show as password field)
   */
  public isSensitiveEnvVar(key: string): boolean {
    const upperKey = key.toUpperCase();
    return this.sensitivePatterns.some(pattern => upperKey.includes(pattern));
  }

  /**
   * Check if a sensitive env var's value is currently visible
   */
  public isEnvVarVisible(serverName: string, key: string): boolean {
    return this.visibleEnvVars.has(`${serverName}:${key}`);
  }

  /**
   * Toggle visibility of a sensitive env var's value
   */
  public toggleEnvVarVisibility(serverName: string, key: string): void {
    const id = `${serverName}:${key}`;
    if (this.visibleEnvVars.has(id)) {
      this.visibleEnvVars.delete(id);
    } else {
      this.visibleEnvVars.add(id);
    }
  }

  /**
   * Update an env var value for a server
   */
  public updateEnvVar(server: McpServerOverride, key: string, value: string): void {
    server.env[key] = value;
  }

  /**
   * Add a new env var to a server
   */
  public async addEnvVar(server: McpServerOverride): Promise<void> {
    const key = await this.dialogs.prompt('Enter environment variable name:');
    if (key && key.trim()) {
      server.env[key.trim()] = '';
    }
  }

  /**
   * Remove an env var from a server
   */
  public async removeEnvVar(server: McpServerOverride, key: string): Promise<void> {
    if (await this.dialogs.confirm(`Remove environment variable "${key}"?`)) {
      delete server.env[key];
    }
  }

  /**
   * Add a new MCP server override
   */
  public async addServerOverride(): Promise<void> {
    const name = await this.dialogs.prompt('Enter MCP server name (e.g., "jira-myproject"):');
    if (name && name.trim()) {
      this.mcpServerOverrides.push({
        name: name.trim(),
        command: '',
        args: [],
        env: {},
        expanded: true
      });
    }
  }

  /**
   * Remove an MCP server override
   */
  public async removeServerOverride(server: McpServerOverride): Promise<void> {
    if (await this.dialogs.confirm(`Remove MCP server "${server.name}"?`)) {
      const index = this.mcpServerOverrides.indexOf(server);
      if (index >= 0) {
        this.mcpServerOverrides.splice(index, 1);
      }
    }
  }

  /**
   * Get display format for args array
   */
  public getArgsDisplay(args: string[]): string {
    return args.join(' ');
  }

  /**
   * Update args from string input
   */
  public updateArgs(server: McpServerOverride, argsString: string): void {
    // Simple split by space - for complex args with spaces, users need JSON edit
    server.args = argsString.split(/\s+/).filter(a => a.length > 0);
  }

  // ============ Responsibilities (Stimuli) Methods ============

  /**
   * Serialize agentStimuli back to the heartbeat_stimuli wire format.
   */
  public _serializeStimuli(): any[] {
    return this.agentStimuli.map(s => ({
      id: s.id,
      plugin: s.plugin,
      params: s.params,
      enabled: s.enabled,
      ...(s.label ? { label: s.label } : {}),
      ...(s.instruction ? { instruction: s.instruction } : {})
    }));
  }

  public get selectedPlugin(): MuxAgentService.StimulusPlugin | null {
    if (!this.stimulusForm) return null;
    return this.availableStimuliPlugins.find(p => p.id === this.stimulusForm!.plugin) || null;
  }

  public openAddForm(): void {
    const defaultPlugin = this.availableStimuliPlugins[0]?.id || '';
    this.stimulusForm = {
      plugin: defaultPlugin,
      label: '',
      instruction: '',
      enabled: true,
      params: this._defaultParamsFor(defaultPlugin),
      editingId: null
    };
  }

  public openEditForm(stimulus: MuxAgentService.AgentStimulus): void {
    this.stimulusForm = {
      plugin: stimulus.plugin,
      label: stimulus.label || '',
      instruction: stimulus.instruction || '',
      enabled: stimulus.enabled,
      params: { ...stimulus.params },
      editingId: stimulus.id
    };
  }

  public closeForm(): void {
    this.stimulusForm = null;
  }

  public onFormPluginChange(): void {
    if (!this.stimulusForm) return;
    this.stimulusForm.params = this._defaultParamsFor(this.stimulusForm.plugin);
  }

  public saveStimulus(): void {
    if (!this.stimulusForm) return;
    const form = this.stimulusForm;
    if (!form.plugin) return;

    if (form.editingId) {
      // Update existing
      const idx = this.agentStimuli.findIndex(s => s.id === form.editingId);
      if (idx >= 0) {
        const plugin = this.availableStimuliPlugins.find(p => p.id === form.plugin);
        this.agentStimuli[idx] = {
          ...this.agentStimuli[idx],
          plugin: form.plugin,
          plugin_name: plugin?.name || form.plugin,
          plugin_description: plugin?.description,
          label: form.label || undefined,
          instruction: form.instruction || undefined,
          enabled: form.enabled,
          params: form.params
        };
      }
    } else {
      // Add new
      const plugin = this.availableStimuliPlugins.find(p => p.id === form.plugin);
      const id = `${form.plugin}-${Date.now()}`;
      this.agentStimuli.push({
        id,
        plugin: form.plugin,
        plugin_name: plugin?.name || form.plugin,
        plugin_description: plugin?.description,
        label: form.label || undefined,
        instruction: form.instruction || undefined,
        enabled: form.enabled,
        params: form.params
      });
    }

    this.stimulusForm = null;
  }

  public removeStimulus(stimulus: MuxAgentService.AgentStimulus): void {
    const idx = this.agentStimuli.indexOf(stimulus);
    if (idx >= 0) {
      this.agentStimuli.splice(idx, 1);
    }
  }

  public toggleStimulusEnabled(stimulus: MuxAgentService.AgentStimulus): void {
    stimulus.enabled = !stimulus.enabled;
  }

  public getStimulusTriggerLabel(stimulus: MuxAgentService.AgentStimulus): string {
    if (stimulus.plugin === 'schedule') {
      const cron = stimulus.params?.['cron_expression'];
      const tz = stimulus.params?.['timezone'] || 'UTC';
      return cron ? `${cron} (${tz})` : 'Schedule (unconfigured)';
    }
    return stimulus.plugin_name;
  }

  public formatLastTriggered(iso: string | undefined): string {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  private _defaultParamsFor(pluginId: string): Record<string, any> {
    const plugin = this.availableStimuliPlugins.find(p => p.id === pluginId);
    if (!plugin) return {};
    const params: Record<string, any> = {};
    for (const p of plugin.parameters) {
      if (p.default !== undefined) {
        params[p.name] = p.default;
      } else {
        params[p.name] = '';
      }
    }
    return params;
  }

  // ============ Skills UI Methods ============

  /**
   * Check if a skill is enabled (not in disabled list).
   * Personal (non-manageable) skills are always considered enabled.
   */
  public isSkillEnabled(skillName: string): boolean {
    return !this.disabledSkills.includes(skillName);
  }

  /**
   * Returns true if a skill can be toggled (it's a team skill, not a personal local dir).
   */
  public isSkillManageable(skillName: string): boolean {
    const skill = this.availableSkills.find((s: any) => s.name === skillName);
    return skill?.manageable !== false;
  }

  /**
   * Toggle a skill's enabled state. No-ops for non-manageable personal skills.
   */
  public toggleSkill(skillName: string): void {
    if (!this.isSkillManageable(skillName)) return;
    const index = this.disabledSkills.indexOf(skillName);
    if (index >= 0) {
      // Currently disabled, enable it
      this.disabledSkills.splice(index, 1);
    } else {
      // Currently enabled, disable it
      this.disabledSkills.push(skillName);
    }
  }
}
